import { inject, Injectable } from '@angular/core';
import { InvoiceMapper, OrganizationStatus, PlanMapper, SubscriptionMapper, SubscriptionStatus } from '@processpuzzle/platform-admin';
import { CustomerAccount, CustomerApplication } from './customer-account';
import { BillingParty, CustomerType, PostalAddress } from './signup';

/**
 * Reads a customer account off the wire.
 *
 * The three billing shapes are mapped by platform-admin's own mappers rather than by code here.
 * That is the same decision the models make, one layer down: they are the same contract shapes on
 * both sides after the schema move, so a second mapper would be a second reading of one payload —
 * and the one that drifted would be the one producing a wrong figure on an invoice.
 *
 * The invoiced party is mapped here, because it is this library's model: the funnel writes it, the
 * portal edits it, and the staff screen never renders it.
 */
@Injectable({ providedIn: 'root' })
export class CustomerAccountMapper {
  private readonly subscriptionMapper = inject(SubscriptionMapper);
  private readonly planMapper = inject(PlanMapper);
  private readonly invoiceMapper = inject(InvoiceMapper);

  fromApplicationDto(dto: unknown): CustomerApplication {
    const source = dto as Record<string, unknown> | null | undefined;
    return {
      orgKey: (source?.['orgKey'] as string) ?? '',
      organizationName: source?.['organizationName'] as string | undefined,
      status: (source?.['status'] as OrganizationStatus | undefined) ?? OrganizationStatus.PROVISIONING,
      applicationUrl: source?.['applicationUrl'] as string | undefined,
      subscriptionStatus: source?.['subscriptionStatus'] as SubscriptionStatus | undefined,
      planName: source?.['planName'] as string | undefined,
    };
  }

  fromDto(dto: unknown): CustomerAccount {
    const source = dto as Record<string, unknown> | null | undefined;
    return {
      orgKey: (source?.['orgKey'] as string) ?? '',
      organizationName: source?.['organizationName'] as string | undefined,
      applicationUrl: source?.['applicationUrl'] as string | undefined,
      status: (source?.['status'] as OrganizationStatus | undefined) ?? OrganizationStatus.PROVISIONING,
      // Each part is mapped only when present, and none implies another. A tenant bootstrapped
      // rather than bought has no subscription; a subscription can name a plan withdrawn from the
      // catalogue; an invoiced party outlives the subscription that justified it by ten years.
      subscription: source?.['subscription'] ? this.subscriptionMapper.fromDto(source['subscription']) : undefined,
      plan: source?.['plan'] ? this.planMapper.fromDto(source['plan']) : undefined,
      billingParty: source?.['billingParty'] ? this.toBillingParty(source['billingParty']) : undefined,
      invoices: ((source?.['invoices'] as unknown[]) ?? []).map((invoice) => this.invoiceMapper.fromDto(invoice)),
    };
  }

  toBillingParty(dto: unknown): BillingParty {
    const source = dto as Partial<BillingParty>;
    return {
      legalName: source.legalName ?? '',
      legalForm: source.legalForm ?? '',
      customerType: source.customerType ?? CustomerType.BUSINESS,
      registeredAddress: this.toAddress(source.registeredAddress),
      // Undefined stays undefined: it means "invoices go to the registered address", and a copy of
      // that address would stop following it the moment the registered one changed.
      billingAddress: source.billingAddress ? this.toAddress(source.billingAddress) : undefined,
      vatId: source.vatId,
      registrationNumber: source.registrationNumber,
      billingEmail: source.billingEmail ?? '',
    };
  }

  private toAddress(address: Partial<PostalAddress> | undefined): PostalAddress {
    return {
      street: address?.street ?? '',
      postalCode: address?.postalCode ?? '',
      city: address?.city ?? '',
      country: address?.country ?? '',
    };
  }
}
